import { useQuery } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin } from 'types'

type UseCertificateGroupParams = {
  certificateId: string
}

type UseCertificateGroupOptions = {
  enabled?: boolean
}

export type CertificateGroupData = {
  /** The group's tagless parent. Null when the certificate isn't part of any group. */
  parent: CertificateAdmin | null
  /** All of the parent's children (the full group, minus the parent). */
  children: CertificateAdmin[]
  /** The requested certificate's siblings (other children of the same parent). */
  siblings: CertificateAdmin[]
}

type UseCertificateGroupResult = {
  data: CertificateGroupData | null
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => Promise<void>
}

// VRG-234/235 — resolves the group (parent + children + siblings) for the given
// certificate, whether it's the group's parent or one of its children. Works from
// EITHER side (see certificates.admin.controller.ts GET groups/:certificateId).
export const useCertificateGroup = (
  params: UseCertificateGroupParams,
  options?: UseCertificateGroupOptions
): UseCertificateGroupResult => {
  const query = useQuery({
    queryKey: ['certificates', 'group', params.certificateId],
    enabled: options?.enabled !== false && Boolean(params?.certificateId),
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/certificates/admin/groups/${params.certificateId}`,
      })

      if (response.status !== 200) {
        throw new Error(`Failed to fetch certificate group ${params.certificateId}`)
      }

      return response.data as CertificateGroupData
    },
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: async () => {
      await query.refetch()
    },
  }
}
